import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  message,
  Modal,
  Tag,
} from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getPageList, updateStatus, deleteUsers } from '../../api/user';
import './index.css';
import dayjs from 'dayjs';

const getField = (record: Record<string, unknown>, key: string): unknown => {
  const v = record[key];
  if (v !== undefined && v !== null) return v;
  const camel = key.charAt(0).toLowerCase() + key.slice(1);
  return record[camel];
};

const TABLE_COLUMNS_CONFIG = [
  { field_code: 'loginId', field_name: '登录ID', width: 120 },
  { field_code: 'username', field_name: '用户名', width: 120 },
  { field_code: 'phone', field_name: '手机号', width: 130 },
  { field_code: 'email', field_name: '邮箱', width: 180 },
  { field_code: 'status', field_name: '状态', width: 80 },
  { field_code: 'isLock', field_name: '锁定', width: 80 },
  { field_code: 'source', field_name: '来源', width: 100 },
  { field_code: 'unionId', field_name: '唯一标识', width: 120 },
  { field_code: 'expiryDate', field_name: '过期时间', width: 170, isTime: true },
  { field_code: 'createTime', field_name: '创建时间', width: 170, isTime: true },
  { field_code: 'updateTime', field_name: '更新时间', width: 170, isTime: true },
];

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 1, label: '启用' },
  { value: 0, label: '禁用' },
];
const LOCK_OPTIONS = [
  { value: '', label: '全部' },
  { value: 0, label: '未锁定' },
  { value: 1, label: '已锁定' },
];

const Users = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    loginId: '',
    username: '',
    phone: '',
    email: '',
    status: '' as '' | 0 | 1,
    isLock: '' as '' | 0 | 1,
    source: '',
    unionId: '',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<Record<string, unknown>[]>([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        pageIndex: pagination.current,
        pageSize: pagination.pageSize,
        loginId: searchParams.loginId || undefined,
        username: searchParams.username || undefined,
        phone: searchParams.phone || undefined,
        email: searchParams.email || undefined,
        status: searchParams.status === '' ? undefined : searchParams.status,
        isLock: searchParams.isLock === '' ? undefined : searchParams.isLock,
        source: searchParams.source || undefined,
        unionId: searchParams.unionId || undefined,
      };
      const res = (await getPageList(payload)) as {
        res_status_code?: string;
        res_content?: {
          totalCount?: number;
          total?: number;
          pageIndex?: number;
          pageSize?: number;
          items?: unknown[];
        };
        res_message?: string;
      };
      if (res?.res_status_code === '0' && res?.res_content != null) {
        const content = res.res_content;
        const items = Array.isArray(content.items) ? content.items : [];
        const total =
          typeof content.totalCount === 'number'
            ? content.totalCount
            : typeof content.total === 'number'
              ? content.total
              : 0;
        setList(items as Record<string, unknown>[]);
        setPagination((prev) => ({ ...prev, total }));
      } else {
        message.error(res?.res_message || '获取用户列表失败');
      }
    } catch (e) {
      console.error('获取用户列表失败', e);
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  const handleReset = () => {
    setSearchParams({
      loginId: '',
      username: '',
      phone: '',
      email: '',
      status: '',
      isLock: '',
      source: '',
      unionId: '',
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current ?? prev.current,
      pageSize: newPagination.pageSize ?? prev.pageSize,
    }));
  };

  const handleBatchStatus = async (status: 0 | 1) => {
    const ids = selectedRows
      .map((r) => r.id ?? (r as { Id?: number }).Id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) {
      message.warning('请先选择要操作的用户');
      return;
    }
    const actionText = status === 1 ? '启用' : '禁用';
    Modal.confirm({
      title: '操作确认',
      content: `确定要批量${actionText}选中的 ${ids.length} 个用户吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = (await updateStatus({ status, ids })) as { res_status_code?: string; res_message?: string };
          if (res?.res_status_code === '0') {
            message.success('操作成功');
            setSelectedRowKeys([]);
            setSelectedRows([]);
            fetchList();
          } else {
            message.error(res?.res_message || '操作失败');
          }
        } catch (e) {
          message.error('操作失败');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleBatchDelete = () => {
    const ids = selectedRows
      .map((r) => r.id ?? (r as { Id?: number }).Id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) {
      message.warning('请先选择要删除的用户');
      return;
    }
    Modal.confirm({
      title: '删除确认',
      content: `确定要删除选中的 ${ids.length} 个用户吗？此为逻辑删除。`,
      okText: '确定',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = (await deleteUsers({ ids })) as { res_status_code?: string; res_message?: string };
          if (res?.res_status_code === '0') {
            message.success('删除成功');
            setSelectedRowKeys([]);
            setSelectedRows([]);
            fetchList();
          } else {
            message.error(res?.res_message || '删除失败');
          }
        } catch (e) {
          message.error('删除失败');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleSingleStatus = (record: Record<string, unknown>, newStatus: 0 | 1) => {
    const id = record.id ?? (record as { Id?: number }).Id;
    if (id == null) {
      message.warning('无法获取用户ID');
      return;
    }
    const actionText = newStatus === 1 ? '启用' : '禁用';
    Modal.confirm({
      title: '操作确认',
      content: `确定要${actionText}该用户吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = (await updateStatus({ status: newStatus, ids: [Number(id)] })) as {
            res_status_code?: string;
            res_message?: string;
          };
          if (res?.res_status_code === '0') {
            message.success('操作成功');
            fetchList();
          } else {
            message.error(res?.res_message || '操作失败');
          }
        } catch (e) {
          message.error('操作失败');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleSingleDelete = (record: Record<string, unknown>) => {
    const id = record.id ?? (record as { Id?: number }).Id;
    if (id == null) {
      message.warning('无法获取用户ID');
      return;
    }
    Modal.confirm({
      title: '删除确认',
      content: '确定要删除该用户吗？此为逻辑删除。',
      okText: '确定',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const res = (await deleteUsers({ ids: [Number(id)] })) as { res_status_code?: string; res_message?: string };
          if (res?.res_status_code === '0') {
            message.success('删除成功');
            fetchList();
          } else {
            message.error(res?.res_message || '删除失败');
          }
        } catch (e) {
          message.error('删除失败');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const columns = [
    ...TABLE_COLUMNS_CONFIG.map((col) => {
      const isTime = col.isTime === true;
      return {
        title: col.field_name,
        dataIndex: col.field_code,
        key: col.field_code,
        width: col.width,
        render: (_: unknown, record: Record<string, unknown>) => {
          let val = getField(record, col.field_code);
          if (col.field_code === 'status') {
            const status = Number(val) === 1  ? 1 : 0;
            const statusMap = {
              0: { text: '禁用', color: 'red' },
              1: { text: '启用', color: 'green' },
            };
            const statusInfo = statusMap[status] || { text: '未知', color: 'default' };
            return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
          }
          if (col.field_code === 'isLock') {
            return val === 1 || val === '1' ? '已锁定' : val === 0 || val === '0' ? '未锁定' : '-';
          }
          if (val == null || val === '') return '-';
          if (isTime) {
            return dayjs(val as string).isValid() ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss') : String(val);
          }
          return String(val);
        },
      };
    }),
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, record: Record<string, unknown>) => {
        const status = getField(record, 'status');
        const numStatus = status === 1 || status === '1' ? 1 : 0;
        const id = record.id ?? (record as { Id?: number }).Id;
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/users/detail/${id}`)}
            >
              编辑
            </Button>
            {numStatus === 1 ? (
              <Button
                type="link"
                size="small"
                onClick={() => handleSingleStatus(record, 0)}
                disabled={actionLoading}
              >
                禁用
              </Button>
            ) : (
              <Button
                type="link"
                size="small"
                onClick={() => handleSingleStatus(record, 1)}
                disabled={actionLoading}
              >
                启用
              </Button>
            )}
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleSingleDelete(record)}
              disabled={actionLoading}
            >
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  const rowKey = (record: Record<string, unknown>) => {
    const id = record.id ?? (record as { Id?: number }).Id;
    return id != null ? String(id) : '';
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h2>用户管理</h2>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/users/new')}>
            新增用户
          </Button>
        </Space>
      </div>

      <div className="search-container">
        <Space size="middle" wrap>
          <Input
            placeholder="登录ID"
            value={searchParams.loginId}
            onChange={(e) => setSearchParams({ ...searchParams, loginId: e.target.value })}
            style={{ width: 160 }}
            allowClear
          />
          <Input
            placeholder="用户名"
            value={searchParams.username}
            onChange={(e) => setSearchParams({ ...searchParams, username: e.target.value })}
            style={{ width: 160 }}
            allowClear
          />
          <Input
            placeholder="手机号"
            value={searchParams.phone}
            onChange={(e) => setSearchParams({ ...searchParams, phone: e.target.value })}
            style={{ width: 160 }}
            allowClear
          />
          <Input
            placeholder="邮箱"
            value={searchParams.email}
            onChange={(e) => setSearchParams({ ...searchParams, email: e.target.value })}
            style={{ width: 180 }}
            allowClear
          />
          <Select
            placeholder="状态"
            value={searchParams.status === '' ? undefined : searchParams.status}
            onChange={(v) => setSearchParams({ ...searchParams, status: v ?? '' })}
            style={{ width: 100 }}
            allowClear
            options={STATUS_OPTIONS}
          />
          <Select
            placeholder="锁定"
            value={searchParams.isLock === '' ? undefined : searchParams.isLock}
            onChange={(v) => setSearchParams({ ...searchParams, isLock: v ?? '' })}
            style={{ width: 100 }}
            allowClear
            options={LOCK_OPTIONS}
          />
          <Input
            placeholder="来源"
            value={searchParams.source}
            onChange={(e) => setSearchParams({ ...searchParams, source: e.target.value })}
            style={{ width: 120 }}
            allowClear
          />
          <Input
            placeholder="唯一标识"
            value={searchParams.unionId}
            onChange={(e) => setSearchParams({ ...searchParams, unionId: e.target.value })}
            style={{ width: 140 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Space>
          <Button
            onClick={() => handleBatchStatus(1)}
            disabled={selectedRowKeys.length === 0 || actionLoading}
          >
            批量启用
          </Button>
          <Button
            onClick={() => handleBatchStatus(0)}
            disabled={selectedRowKeys.length === 0 || actionLoading}
          >
            批量禁用
          </Button>
          <Button
            danger
            onClick={handleBatchDelete}
            disabled={selectedRowKeys.length === 0 || actionLoading}
            icon={<DeleteOutlined />}
          >
            批量删除
          </Button>
          {selectedRowKeys.length > 0 && (
            <span style={{ color: '#666' }}>已选 {selectedRowKeys.length} 条</span>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={list}
        rowKey={rowKey}
        loading={loading}
        size="small"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows as Record<string, unknown>[]);
          },
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          size: 'small',
        }}
        onChange={(pager) => handleTableChange(pager)}
        scroll={{ x: 1600, y: 'calc(100vh - 380px)' }}
      />
    </div>
  );
};

export default Users;
